
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { FaTrophy, FaLightbulb, FaExclamationTriangle, FaChevronRight, FaSpinner } from "react-icons/fa";
import { generateMisinformationQuestions, QuizQuestion } from "@/services/quizService";
import { toast } from "sonner";

const MisinformationGame = () => {
  const [gameState, setGameState] = useState<'start' | 'loading' | 'playing' | 'result'>('start');
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedOption, setSelectedOption] = useState<string | null>(null);
  const [score, setScore] = useState(0);
  const [answers, setAnswers] = useState<{ questionId: number; correct: boolean; selectedOption: string }[]>([]);
  const [showExplanation, setShowExplanation] = useState(false);
  const [questions, setQuestions] = useState<QuizQuestion[]>([]);
  
  const currentQuestion = questions[currentQuestionIndex];
  const progress = ((currentQuestionIndex) / questions.length) * 100;

  // Function to start the game and generate questions using AI
  const startGame = async () => {
    setGameState('loading');
    try {
      // Use the AI service to generate questions
      const generatedQuestions = await generateMisinformationQuestions(5);
      setQuestions(generatedQuestions);
      setCurrentQuestionIndex(0);
      setScore(0);
      setAnswers([]);
      setGameState('playing');
    } catch (error) {
      console.error("Failed to generate questions:", error);
      toast.error("Failed to generate quiz questions. Using default questions instead.");
      setGameState('start');
    }
  };

  const handleOptionSelect = (optionId: string) => {
    if (!currentQuestion) return;
    
    setSelectedOption(optionId);
    setShowExplanation(true);
    
    const isCorrect = optionId === currentQuestion.correctAnswer;
    if (isCorrect) {
      setScore(prev => prev + 1);
    }
    
    setAnswers(prev => [
      ...prev, 
      { 
        questionId: currentQuestion.id, 
        correct: isCorrect,
        selectedOption: optionId
      }
    ]);
  };

  const handleNextQuestion = () => {
    setSelectedOption(null);
    setShowExplanation(false);
    
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(prev => prev + 1);
    } else {
      setGameState('result');
    }
  };

  const resetGame = () => {
    setGameState('start');
    setCurrentQuestionIndex(0);
    setScore(0);
    setSelectedOption(null);
    setAnswers([]);
    setShowExplanation(false);
    setQuestions([]);
  };

  return (
    <div className="w-full max-w-4xl mx-auto">
      {gameState === 'start' && (
        <Card className="bg-black/20 backdrop-blur-sm border-gray-800">
          <CardHeader className="text-center">
            <CardTitle className="text-2xl md:text-3xl">Misinformation Challenge</CardTitle>
            <CardDescription>Test your ability to identify misinformation tactics</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="bg-black/30 p-4 rounded-lg">
              <h3 className="font-medium text-lg mb-2">How to Play</h3>
              <p className="text-gray-300 mb-3">
                You'll be presented with examples of content that might appear in your social media feeds or news sources.
                Each game session will have unique, AI-generated questions to test your misinformation detection skills.
              </p>
              <ul className="space-y-2 text-gray-300">
                <li className="flex items-start gap-2">
                  <FaExclamationTriangle className="text-yellow-500 mt-1 shrink-0" />
                  <span>Analyze each example and identify potential misinformation tactics</span>
                </li>
                <li className="flex items-start gap-2">
                  <FaLightbulb className="text-truth mt-1 shrink-0" />
                  <span>Learn about common manipulation techniques used to spread false information</span>
                </li>
                <li className="flex items-start gap-2">
                  <FaTrophy className="text-yellow-500 mt-1 shrink-0" />
                  <span>Test your critical thinking skills and see how you score</span>
                </li>
              </ul>
            </div>
          </CardContent>
          <CardFooter>
            <Button 
              onClick={startGame} 
              className="w-full bg-truth hover:bg-truth-dark text-white"
            >
              Start Challenge
            </Button>
          </CardFooter>
        </Card>
      )}

      {gameState === 'loading' && (
        <Card className="bg-black/20 backdrop-blur-sm border-gray-800">
          <CardContent className="flex flex-col items-center justify-center py-20">
            <FaSpinner className="text-truth text-4xl animate-spin mb-4" />
            <p>Generating misinformation challenge questions...</p>
            <p className="text-sm text-gray-400 mt-2">Our AI is creating unique questions for you</p>
          </CardContent>
        </Card>
      )}

      {gameState === 'playing' && currentQuestion && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-400">Question {currentQuestionIndex + 1} of {questions.length}</div>
            <div className="text-sm font-medium text-truth">Score: {score}</div>
          </div>
          
          <Progress value={progress} className="h-2 bg-gray-800" />
          
          <Card className="bg-black/20 backdrop-blur-sm border-gray-800">
            <CardHeader>
              <CardTitle className="text-xl">Analyze this content</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="bg-black/30 border border-gray-700 p-4 rounded-lg">
                <p className="text-gray-100">{currentQuestion.content}</p>
              </div>
              
              <div className="space-y-3">
                <h3 className="font-medium">What's happening in this example?</h3>
                {currentQuestion.options.map(option => (
                  <Button
                    key={option.id}
                    variant={selectedOption === option.id ? "default" : "outline"}
                    className={`w-full justify-start text-left h-auto py-3 px-4 font-normal ${
                      selectedOption && showExplanation
                        ? option.id === currentQuestion.correctAnswer
                          ? "border-green-500 bg-green-500/10 hover:bg-green-500/20"
                          : option.id === selectedOption
                          ? "border-red-500 bg-red-500/10 hover:bg-red-500/20"
                          : ""
                        : ""
                    }`}
                    onClick={() => !selectedOption && handleOptionSelect(option.id)}
                    disabled={!!selectedOption}
                  >
                    {option.text}
                  </Button>
                ))}
              </div>
              
              {showExplanation && (
                <div className="bg-truth/10 border border-truth/30 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Badge className="bg-truth hover:bg-truth-dark">{currentQuestion.tactic}</Badge>
                    <h3 className="font-medium">Explanation:</h3>
                  </div>
                  <p className="text-sm text-gray-300">{currentQuestion.explanation}</p>
                </div>
              )}
            </CardContent>
            <CardFooter>
              {showExplanation && (
                <Button 
                  onClick={handleNextQuestion} 
                  className="ml-auto bg-truth hover:bg-truth-dark flex items-center gap-2"
                >
                  {currentQuestionIndex < questions.length - 1 ? "Next Question" : "See Results"}
                  <FaChevronRight size={12} />
                </Button>
              )}
            </CardFooter>
          </Card>
        </div>
      )}

      {gameState === 'result' && (
        <Card className="bg-black/20 backdrop-blur-sm border-gray-800">
          <CardHeader className="text-center">
            <CardTitle className="text-2xl">Your Results</CardTitle>
            <div className="mt-4 flex justify-center">
              <div className="bg-truth/20 p-6 rounded-full relative">
                <div className="text-4xl font-bold text-truth">{score}</div>
                <div className="text-sm text-gray-400">of {questions.length}</div>
                {score === questions.length && (
                  <div className="absolute -top-2 -right-2 bg-yellow-500 text-black text-xs font-bold rounded-full p-1 w-6 h-6 flex items-center justify-center">
                    <FaTrophy />
                  </div>
                )}
              </div>
            </div>
            <CardDescription className="mt-4">
              {score === questions.length && "Perfect score! You're an expert at spotting misinformation."}
              {score >= 3 && score < questions.length && "Great job! You have strong critical thinking skills."}
              {score < 3 && "Good effort! With practice you'll get better at spotting misinformation."}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <h3 className="font-medium mb-3">Question Review</h3>
            <div className="space-y-2">
              {answers.map((answer, index) => {
                const question = questions.find(q => q.id === answer.questionId);
                return (
                  <div 
                    key={index} 
                    className={`p-3 rounded-lg border ${
                      answer.correct 
                        ? "border-green-500/30 bg-green-500/5" 
                        : "border-red-500/30 bg-red-500/5"
                    }`}
                  >
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium">Question {index + 1}</span>
                      <Badge className={answer.correct ? "bg-green-600" : "bg-red-600"}>
                        {answer.correct ? "Correct" : "Incorrect"}
                      </Badge>
                    </div>
                    <div className="mt-1 text-xs text-gray-400">
                      Misinformation tactic: <span className="text-truth">{question?.tactic}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
          <CardFooter className="flex gap-3 justify-end">
            <Button variant="outline" onClick={resetGame}>
              Try Again
            </Button>
            <Button className="bg-truth hover:bg-truth-dark">
              Learn More About Misinformation
            </Button>
          </CardFooter>
        </Card>
      )}
    </div>
  );
};

export default MisinformationGame;
